/**
 * RatiosTab
 *
 * Fariha's monthly ratios report: "calculating individual timekeeper
 * contributions (billable hours entered) to matters. Right now I am running
 * the report for Partners."
 *
 * The title filter runs on normalised title groups rather than raw prof_type,
 * which is fractured across ~160 spellings — "Partner" alone appears nine ways.
 * See utils/profTitles.ts.
 */

import { Check } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
	DATE_PRESET_LABELS,
	type DatePreset,
	getDateRange,
} from "@/features/attorney/hooks/useReportingDashboard";
import { ValueChecklist } from "@/features/attorney/views/Entries/filters/ValueChecklist";
import { cn } from "@/shared/lib/utils";
import { DatePicker } from "@/v2/components/date-picker/DatePicker";
import { Button } from "@/v2/components/ui/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/v2/components/ui/popover";
import { useRatios } from "../../hooks/useReportsData";
import type { RatioRow } from "../../types/reports";
import type { ProfTitleGroup } from "../../utils/profTitles";
import RatiosChart from "./RatiosChart";
import ReportKpiRow, { type KpiCardSpec } from "./ReportKpiRow";
import ReportTable, { formatHours, type ReportColumn } from "./ReportTable";

/** Matters charted; the rest are reachable through the matter filter. */
const CHART_LIMIT = 15;
/** Rows rendered before "Show more" — the table is not virtualized. */
const PAGE_SIZE = 100;

const columns: Array<ReportColumn<RatioRow>> = [
	{ key: "name", header: "Timekeeper", cell: (r) => r.profName },
	{ key: "type", header: "Title", width: "9rem", cell: (r) => r.profType },
	{
		key: "matter",
		header: "Matter",
		cell: (r) => r.matterDescription || r.matterNumber,
	},
	{ key: "client", header: "Client", cell: (r) => r.client },
	{
		key: "hours",
		header: "Hours",
		align: "right",
		width: "6rem",
		cell: (r) => formatHours(r.hours),
	},
	{
		key: "share",
		header: "Share",
		align: "right",
		width: "6rem",
		cell: (r) => `${r.sharePct}%`,
	},
];

function currentMonth(): { from: Date; to: Date } {
	const now = new Date();
	return {
		from: new Date(now.getFullYear(), now.getMonth(), 1),
		to: new Date(now.getFullYear(), now.getMonth() + 1, 0),
	};
}

export default function RatiosTab() {
	const [dateRange, setDateRange] = useState(currentMonth);
	// Defaults to Partner: "Right now I am running the report for Partners."
	const [profType, setProfType] = useState<ProfTitleGroup | null>("Partner");
	// null is implicit "all matters", matching ValueChecklist's contract.
	const [matters, setMatters] = useState<string[] | null>(null);
	const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

	const { data, loading, error } = useRatios(
		dateRange.from,
		dateRange.to,
		profType,
		matters,
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: reset paging when the result set changes.
	useEffect(() => {
		setVisibleCount(PAGE_SIZE);
	}, [dateRange, profType, matters]);

	const activePreset = useMemo(() => {
		const keys = Object.keys(DATE_PRESET_LABELS) as DatePreset[];
		return keys.find((key) => {
			const r = getDateRange(key);
			return (
				r.from.toDateString() === dateRange.from.toDateString() &&
				r.to.toDateString() === dateRange.to.toDateString()
			);
		});
	}, [dateRange]);

	// Options come from every matter with activity, not just the filtered set,
	// so deselecting one does not remove it from the list.
	const matterOptions = useMemo(() => {
		const seen = new Map<
			string,
			{ value: string; label: string; sublabel?: string }
		>();
		for (const r of data?.rows ?? []) {
			if (!seen.has(r.matterId)) {
				seen.set(r.matterId, {
					value: r.matterId,
					label: r.matterDescription || r.matterNumber,
					sublabel: r.client,
				});
			}
		}
		return [...seen.values()].sort((a, b) => a.label.localeCompare(b.label));
	}, [data?.rows]);

	const cards: KpiCardSpec[] = data
		? [
				{
					label: "Matters",
					value: String(data.matters.length),
					gradient: "green",
				},
				{
					label: "Timekeepers",
					value: String(new Set(data.rows.map((r) => r.profId)).size),
					gradient: "teal",
				},
				{
					label: "Billable hours",
					value: formatHours(data.rows.reduce((sum, r) => sum + r.hours, 0)),
					suffix: "hrs",
					gradient: "dark",
				},
			]
		: [];

	return (
		<div className="flex flex-col gap-6">
			<div className="flex flex-wrap items-end gap-3">
				<div className="flex flex-col gap-1.5">
					<span className="text-xs font-medium text-muted-foreground">
						Period
					</span>
					<DatePicker
						className="w-fit"
						dateRange={dateRange}
						onDateChange={(day) => setDateRange({ from: day, to: day })}
						onDateRangeChange={(range) =>
							setDateRange({ from: range.from, to: range.to })
						}
						selectionMode="range"
						sideSlot={({ selectRange }) => (
							<div className="flex w-[160px] flex-col border-r p-1">
								{Object.entries(DATE_PRESET_LABELS).map(([key, label]) => {
									const range = getDateRange(key as DatePreset);
									return (
										<button
											key={key}
											type="button"
											className={cn(
												"flex items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted",
												activePreset === key && "bg-muted font-medium",
											)}
											onClick={() => selectRange(range)}
										>
											<Check
												className={cn(
													"h-3.5 w-3.5 shrink-0",
													activePreset === key ? "opacity-100" : "opacity-0",
												)}
											/>
											{label}
										</button>
									);
								})}
							</div>
						)}
					/>
				</div>

				<div className="flex flex-col gap-1.5">
					<span className="text-xs font-medium text-muted-foreground">
						Title
					</span>
					<Popover>
						<PopoverTrigger asChild>
							<Button
								variant="outline"
								className="w-48 justify-start font-normal"
							>
								{profType ?? "All titles"}
							</Button>
						</PopoverTrigger>
						<PopoverContent align="start" className="w-48 p-1">
							<button
								type="button"
								onClick={() => setProfType(null)}
								className={cn(
									"flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted",
									profType === null && "bg-muted font-medium",
								)}
							>
								<Check
									className={cn(
										"h-3.5 w-3.5 shrink-0",
										profType === null ? "opacity-100" : "opacity-0",
									)}
								/>
								All titles
							</button>
							{(data?.availableTypes ?? []).map((t) => (
								<button
									key={t}
									type="button"
									onClick={() => setProfType(t)}
									className={cn(
										"flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-muted",
										profType === t && "bg-muted font-medium",
									)}
								>
									<Check
										className={cn(
											"h-3.5 w-3.5 shrink-0",
											profType === t ? "opacity-100" : "opacity-0",
										)}
									/>
									{t}
								</button>
							))}
						</PopoverContent>
					</Popover>
				</div>

				<div className="flex flex-col gap-1.5">
					<span className="text-xs font-medium text-muted-foreground">
						Matters
					</span>
					<Popover>
						<PopoverTrigger asChild>
							<Button
								variant="outline"
								className="w-64 justify-start font-normal"
							>
								{matters === null
									? `All matters (${matterOptions.length})`
									: matters.length === 0
										? "None selected"
										: `${matters.length} selected`}
							</Button>
						</PopoverTrigger>
						<PopoverContent align="start" className="w-80 p-2">
							<ValueChecklist
								field="matter"
								options={matterOptions}
								selectedValues={matters}
								onToggle={(value) =>
									setMatters((prev) => {
										if (prev === null) {
											return matterOptions
												.map((o) => o.value)
												.filter((v) => v !== value);
										}
										return prev.includes(value)
											? prev.filter((v) => v !== value)
											: [...prev, value];
									})
								}
								onSelectAll={() => setMatters(null)}
								onSelectNone={() => setMatters([])}
							/>
						</PopoverContent>
					</Popover>
				</div>
			</div>

			{error && (
				<div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
					{error}
				</div>
			)}

			{loading && !data && (
				<div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">
					Loading…
				</div>
			)}

			{data && (
				<>
					<ReportKpiRow cards={cards} />

					{data.matters.length > 0 && (
						<div className="rounded-lg border border-border p-4">
							<h2 className="mb-3 text-sm font-medium text-foreground">
								{data.matters.length > CHART_LIMIT
									? `Top ${CHART_LIMIT} matters by hours`
									: "Hours by matter"}
							</h2>
							<RatiosChart matters={data.matters} limit={CHART_LIMIT} />
						</div>
					)}

					<ReportTable
						columns={columns}
						rows={data.rows.slice(0, visibleCount)}
						rowKey={(r) => `${r.profId}:${r.matterId}`}
						isStale={loading}
						emptyMessage="No timekeeper contributions for this period."
					/>

					{data.rows.length > visibleCount && (
						<div className="flex flex-col items-center gap-1">
							<Button
								variant="outline"
								onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
							>
								Show {Math.min(PAGE_SIZE, data.rows.length - visibleCount)} more
							</Button>
							<p className="text-xs text-muted-foreground">
								Showing {visibleCount} of {data.rows.length} rows
							</p>
						</div>
					)}
				</>
			)}
		</div>
	);
}
